import { defaultUrlTransform } from 'react-markdown';
import { whatIfFromParams } from '@/pages/b2b/tools/amortizationSchedule/whatIf';
import { B2C_PROFILE_STEP_KEYS } from '@/pages/b2c/profile/steps';

// Resolver for `mira:` deep links the B2B agent emits inside chat markdown.
// The agent emits intent (a page/tab key); the frontend owns the real route so
// tenant host, pipeline-vs-loanInformation, and tab numbering stay out of the LLM.
// Tab keys resolve to `/pipeline/lead?applicationId=…&tab=…`; the borrower-profile
// shell coerces a tab that is illegal at the loan's stage (coerceTabForStage), so
// this resolver never has to fetch status or produce a dead tab.

export const MIRA_SCHEME = 'mira:';

// Left-nav pages: normalized key -> app-relative path. Aliases cover both the
// camelCase path form and a kebab form so the agent can emit either.
const STATIC_PAGES = {
  dashboard: '/dashboard',
  loaninformation: '/loanInformation',
  'loan-information': '/loanInformation',
  pipeline: '/pipeline',
  quickprice: '/quickPrice',
  'quick-price': '/quickPrice',
  tools: '/tools',
  reports: '/reports',
  'admin/employees': '/admin/employees',
  'admin/consumers': '/admin/consumers',
  'admin/branches': '/admin/branches',
  'admin/invitations': '/admin/invitations',
  'help-support': '/help-support',
  helpsupport: '/help-support',
};

// ---------------------------------------------------------------------------
// B2C (borrower-facing app). Same scheme, different surfaces: five sidebar pages
// plus the Profile steps and Tools tabs. Unlike a B2B profile tab, every page here
// has its own application selector, so applicationId is optional — a link without
// one still lands somewhere useful, which is what the agent emits when no specific
// application is in the conversation.
const B2C_PAGES = {
  dashboard: '/dashboard',
  profile: '/profile',
  documents: '/documents',
  tools: '/tools',
  chat: '/chat',
  // The borrower-facing label is "My Applications"; the route is still /pipeline.
  'my-applications': '/pipeline',
  myapplications: '/pipeline',
  'support-docs': '/support-docs',
  supportdocs: '/support-docs',
  'help-support': '/help-support',
  helpsupport: '/help-support',
};

// Profile step key -> step id. Imported rather than restated: the profile
// page, the /fees-worksheet redirect and this resolver all read one table, so
// adding or reordering a tab cannot leave a chat link pointing at the old one.
const B2C_PROFILE_STEPS = B2C_PROFILE_STEP_KEYS;

// Tools tab key -> tab id (pages/b2c/tools/index.jsx).
const B2C_TOOLS_TABS = {
  'loan-comparison': 1,
  amortization: 2,
  'amortization-schedule': 2,
};

// Borrower-profile tab key -> tab id (mirrors BORROWER_SHELL_TABS in stageUtils.js).
const TAB_KEYS = {
  'whats-next': 13,
  'borrower-profile': 1,
  documents: 2,
  'products-pricing': 3,
  'fees-worksheet': 4,
  'credit-setup': 5,
  aus: 6,
  'pre-underwriting': 7,
};

// applicationId is the loan application's UUID. An `ML-…` display id or any other
// shape would 404 the profile shell, so treat it as unresolvable (plain text)
// rather than rendering a link that navigates nowhere.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isMiraLink = (href) =>
  typeof href === 'string' && href.trim().toLowerCase().startsWith(MIRA_SCHEME);

// react-markdown sanitizes hrefs via defaultUrlTransform, which blanks any
// non-standard scheme (mira: → ''). Let mira: links through untouched and keep
// the default (safe) handling for everything else. Pass to <Markdown urlTransform=…>.
export const miraUrlTransform = (url) =>
  isMiraLink(url) ? url : defaultUrlTransform(url);

// Tools tab key -> tab id. Shared shape with B2C_TOOLS_TABS; the two pages number their
// tabs the same way, so a comparison link resolves identically on either portal.
const B2B_TOOLS_TABS = {
  'loan-comparison': 1,
  amortization: 2,
  'amortization-schedule': 2,
};

// An amortization link may carry the what-if Mira AI just answered (extra_monthly=200,
// lump_sum=10000&lump_sum_month=60, ...) so the schedule opens on that scenario. Only
// the validated levers pass through, and only to the amortization tab.
const AMORTIZATION_TAB = 2;
function copyWhatIf(params, qs) {
  const whatIf = whatIfFromParams(params);
  if (!whatIf) return;
  Object.entries(whatIf).forEach(([key, value]) => {
    if (value) qs.set(key, value);
  });
}

function resolveB2bLink(key, params) {
  // Tools carries an application and a tab, so a comparison link lands on the one it
  // describes rather than on whichever loan the picker defaulted to.
  if (key === 'tools') {
    const qs = new URLSearchParams();
    const applicationId = params.get('applicationId');
    if (applicationId && UUID_RE.test(applicationId)) qs.set('applicationId', applicationId);
    const tab = B2B_TOOLS_TABS[(params.get('tab') || '').toLowerCase()];
    if (tab) qs.set('tab', String(tab));
    if (tab === AMORTIZATION_TAB) copyWhatIf(params, qs);
    const query = qs.toString();
    return { path: query ? `/tools?${query}` : '/tools' };
  }

  if (STATIC_PAGES[key]) return { path: STATIC_PAGES[key] };

  if (Object.prototype.hasOwnProperty.call(TAB_KEYS, key)) {
    const applicationId = params.get('applicationId');
    // No application in scope, or not a real application id → no deep link.
    if (!applicationId || !UUID_RE.test(applicationId)) return null;
    const qs = new URLSearchParams({ applicationId, tab: String(TAB_KEYS[key]) });
    return { path: `/pipeline/lead?${qs.toString()}` };
  }

  return null;
}

function resolveB2cLink(key, params) {
  const path = B2C_PAGES[key];
  if (!path) return null;

  const qs = new URLSearchParams();
  const applicationId = params.get('applicationId');
  // An id that isn't a real application would leave the page loading nothing, so
  // drop it and let the page fall back to its own selector.
  if (applicationId && UUID_RE.test(applicationId)) qs.set('applicationId', applicationId);

  if (path === '/profile') {
    const step = B2C_PROFILE_STEPS[(params.get('step') || '').toLowerCase()];
    if (step) qs.set('step', String(step));
  }
  if (path === '/tools') {
    const tab = B2C_TOOLS_TABS[(params.get('tab') || '').toLowerCase()];
    if (tab) qs.set('tab', String(tab));
    if (tab === AMORTIZATION_TAB) copyWhatIf(params, qs);
  }

  const query = qs.toString();
  return { path: query ? `${path}?${query}` : path };
}

// Returns { path } for a page link, { documentId } for a document link, or null
// when the link can't be built (unknown key, a B2B tab key with no applicationId,
// or a document id that isn't one) — the caller renders plain text in that case
// so a broken link is never shown.
export function resolveMiraLink(href, portal = 'b2b') {
  if (!isMiraLink(href)) return null;

  // Tolerate `mira:page/…` and `mira://page/…`.
  const rest = href.trim().slice(MIRA_SCHEME.length).replace(/^\/+/, '');
  const [pathPart, queryPart = ''] = rest.split('?');

  // `mira:doc/<documentId>` — a file the agent read, opened the way the
  // Documents tab's eye opens it. Both portals: MiraChatLink fetches it through
  // the signed-in portal's own download (lender or borrower), which checks the
  // caller may see that file.
  const docMatch = pathPart.match(/^doc\/([^/]+)$/i);
  if (docMatch) {
    if (!UUID_RE.test(docMatch[1])) return null;
    return { documentId: docMatch[1] };
  }

  const match = pathPart.match(/^page\/(.+)$/i);
  if (!match) return null;

  const key = match[1].toLowerCase();
  const params = new URLSearchParams(queryPart);

  return portal === 'b2c' ? resolveB2cLink(key, params) : resolveB2bLink(key, params);
}
